import { memo } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

import { resolveAsked } from './Asked';
import { useCan } from './useCan';

import type { ResourceFacts } from '../core';
import type { Asked } from './Asked';

type Props = PropsWithChildren<
  Asked & {
    facts?: ResourceFacts;
    /** Rendered when the answer is no. Nothing by default. */
    fallback?: ReactNode;
    /** Rendered while there is no answer yet. Nothing by default - see below. */
    loading?: ReactNode;
    /**
     * Rendered when the answer could not be determined. Falls back to `fallback` when absent, which
     * is today's behaviour and the right default (R19).
     *
     * Opt-in because the rule is per-surface and the default is silence: an inline affordance denies
     * quietly, exactly as it does now. Only a surface where silence is itself misleading passes this
     * - the dashboard's navigation region, which is entirely inline affordances and would otherwise
     * render as an empty sidebar that reads as revoked access (AE10).
     */
    unanswerable?: ReactNode;
  }
>;

/**
 * The render gate, for the case that does not need to see `isLoading` at all:
 *
 * ```tsx
 * <Can action="community:manage">
 *   <EditCommunityButton />
 * </Can>
 * ```
 *
 * Renders nothing while the answer is pending, which is what the sidebar and the Add button expect.
 * Pass `loading={<CircularProgress />}` to show a spinner instead.
 *
 * An unreachable answer renders `fallback` unless the caller passes `unanswerable`, so a failed
 * fetch denies silently exactly as it does now. That default is deliberate: R19 keeps inline
 * affordances quiet, because a warning per hidden button would be worse than the button's absence.
 * The one exception is a surface where absence is itself the misleading message - see `unanswerable`.
 */
export const Can = memo(
  ({ action, all, any, facts, fallback = null, loading = null, unanswerable, children }: Props) => {
    const { actions, mode } = resolveAsked({ action, all, any });
    const { allowed, isLoading, isError } = useCan(actions, facts, mode);

    if (isLoading) return <>{loading}</>;
    // Only when the caller asked for it. Without `unanswerable` this falls through to `fallback`,
    // so an undetermined answer denies silently as it always has.
    if (isError && unanswerable !== undefined) return <>{unanswerable}</>;

    return <>{allowed ? children : fallback}</>;
  }
);
